import { usePermission, type IPermissionCode } from "@/shared/permission";
import type { BusinessType } from "./types";

type PermissionKey =
  | "add"
  | "main"
  | "marketing"
  | "userList"
  | "businessList"
  | "businessEdit"
  | "businessDetail"
  | "changeStatus";

/** The permission code behind every action, so a disabled control can name what is missing. */
export const BUSINESS_PERMISSION_CODES: Record<BusinessType, Record<PermissionKey, IPermissionCode>> = {
  SUPPLIER: {
    add: "PRT_SUPP_ADD",
    main: "PRT_SUPP_MAIN",
    marketing: "PRT_SUPP_MARKETING",
    userList: "PRT_SUPP_USER_LIST",
    businessList: "PRT_SUPP_BUSINESS_LIST",
    businessEdit: "PRT_SUPP_BUSINESS_EDIT",
    businessDetail: "PRT_SUPP_BUSINESS_DETAIL",
    changeStatus: "PRT_SUPP_BUSINESS_CHANGE_STATUS",
  },
  BUYER: {
    add: "PRT_BUYER_ADD",
    main: "PRT_BUYER_MAIN",
    marketing: "PRT_BUYER_MARKETING",
    userList: "PRT_BUYER_USER_LIST",
    businessList: "PRT_BUYER_BUSINESS_LIST",
    businessEdit: "PRT_BUYER_BUSINESS_EDIT",
    businessDetail: "PRT_BUYER_BUSINESS_DETAIL",
    changeStatus: "PRT_BUYER_BUSINESS_CHANGE_STATUS",
  },
};

function usePermissionSet(type: BusinessType) {
  const codes = BUSINESS_PERMISSION_CODES[type];
  return {
    codes,
    add: usePermission(codes.add),
    main: usePermission(codes.main),
    marketing: usePermission(codes.marketing),
    userList: usePermission(codes.userList),
    businessList: usePermission(codes.businessList),
    businessEdit: usePermission(codes.businessEdit),
    businessDetail: usePermission(codes.businessDetail),
    changeStatus: usePermission(codes.changeStatus),
  };
}

/**
 * Both types' permissions are read on every render (hooks cannot be conditional), the
 * requested type's set is returned.
 */
export function useBusinessPermissions(type: BusinessType) {
  const supplier = usePermissionSet("SUPPLIER");
  const buyer = usePermissionSet("BUYER");
  return type === "SUPPLIER" ? supplier : buyer;
}

export type BusinessPermissions = ReturnType<typeof useBusinessPermissions>;

/** Whether the user may open the list of each type (drives the type switch). */
export function useBusinessListAccess(): Record<BusinessType, boolean> {
  const supplier = usePermission(BUSINESS_PERMISSION_CODES.SUPPLIER.businessList);
  const buyer = usePermission(BUSINESS_PERMISSION_CODES.BUYER.businessList);
  return { SUPPLIER: supplier.hasAccess, BUYER: buyer.hasAccess };
}
